<template>
  <div class="scan-qr-code">
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        class="scan-qr-code !w-[650px] !max-w-[650px]"
        :show-close-button="false"
        @open-auto-focus.prevent
        @escape-key-down.prevent
        @pointer-down-outside.prevent
      >
        <button
          type="button"
          class="absolute top-4 right-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none"
          :aria-label="t('button.close')"
          @click="closeModal"
        >
          <XIcon class="h-5 w-5" />
        </button>
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ t("qrcode.scan-booking") }}
          </SDialogTitle>
        </SDialogHeader>
        <div v-if="loading" class="spinner-overlay">
          <LoaderCircleIcon class="s-icon-spin spinner" />
        </div>
        <div v-else class="d-flex fl-col fl-j-c fl-a-c">
          <div class="container">
            <p class="scan-booking">{{ t("qrcode.scan-booking") }}</p>
            <p class="qrcode-instruction">{{ t("qrcode.instruction") }}</p>
            <img
              src="@/assets/images/icons/qrcode-scan.png"
              class="qrcode-img"
            />
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import NotificationService from "@/services/notification.service";
import { ModalStatus } from "@/constants/modal";
import { LogService } from "@/services/logging";
import ScannerModule from "@/store/modules/scanner";
import { getMembershipTokenByVoucherCode } from "@/api/gql/product-slot-state";
import ModalModule from "@/store/modules/modal";
import { openOrder } from "@/utils/vue-helpers/orders";
import { fetchOrderByToken } from "@/utils/vue-helpers/orders.helper";
import { extractCodeFromUrl } from "@/utils/vue-helpers/promocode";
import POSConfigModule from "@/store/modules/pos-config";
import { LoaderCircleIcon, XIcon } from "@smeetz/ui-icons";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "ScanQrCode",
  components: {
    LoaderCircleIcon,
    XIcon,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const { t } = useI18n({});
    const loading = ref(false);
    const modalVisibility = dialogVisibleForStatus(ModalStatus.ScanQrCode);
    // scanner
    const ScannerTog = computed(() => {
      return ScannerModule.tog;
    });

    watch(
      ScannerTog,
      async () => {
        if (
          ScannerModule.value &&
          modalVisibility.value &&
          POSConfigModule.groupId
        ) {
          loading.value = true;
          const scannedPSSQRCode = ScannerModule.value;
          let scannedPSSToken = scannedPSSQRCode;
          LogService.info(`QR Code: PSS QRCode scanned = ${scannedPSSQRCode}`);
          try {
            const isUrl =
              String(scannedPSSQRCode).startsWith("http://") ||
              String(scannedPSSQRCode).startsWith("https://") ||
              String(scannedPSSQRCode).startsWith("http//") ||
              String(scannedPSSQRCode).startsWith("https//");
            if (isUrl) {
              LogService.info(`QR Code: scanned value is in url format`);
              // We need to extract the voucher code from the url scanned, then try to pull the curresponding membership ticket.
              const code = extractCodeFromUrl(ScannerModule.value);
              LogService.info(
                `QR Code: Voucher code extracted from qrcode string code: ${code}`
              );
              if (code && code.length >= 5) {
                LogService.info(
                  `QR Code: going to fetch the token of the membership ticket with code: ${code}`
                );
                const membershipToken = await getMembershipTokenByVoucherCode(
                  code,
                  POSConfigModule.groupId
                );
                if (membershipToken) {
                  LogService.info(
                    `QR Code: got membership token from scanned voucher code`,
                    { membershipToken }
                  );
                  scannedPSSToken = membershipToken;
                } else {
                  LogService.info(
                    `QR Code: no membership token was found for scanned voucher code`,
                    { code }
                  );
                  NotificationService.showCustomError(t("qrcode.not-found"));
                  return;
                }
              }
            } else {
              // Check if the scanned qr code has the new json format of membership tickets.
              // Try to parse the QR code as JSON.
              try {
                const scannedJsonCode = JSON.parse(scannedPSSQRCode);
                if (
                  scannedJsonCode &&
                  scannedJsonCode.code &&
                  scannedJsonCode.token
                ) {
                  LogService.info(
                    `QR Code: scanned token is a membership ticket with json format.`,
                    { params: scannedJsonCode }
                  );
                  scannedPSSToken = scannedJsonCode.token;
                }
              } catch (parseError) {
                // If JSON.parse fails, let's handle the QR code as if it's a normal token.
                LogService.info(`QR Code: scanned token is a normal ticket.`);
              }
            }
            // Resolve the scanned ticket token to its order and open it in the
            // order flow (replaces the legacy getPSSWithToken → BookingId path).
            const order = await fetchOrderByToken(scannedPSSToken);
            LogService.info(`QR Code: got order from scanned token`, {
              reference: order.reference,
            });
            NotificationService.showCustomSuccess(t("qrcode.success"));
            ModalModule.setStatus(ModalStatus.Hidden);
            await openOrder({ reference: order.reference });
          } catch (error) {
            LogService.info(`QR Code: Failed to fetch PSS`, {
              scannedPSS: scannedPSSToken,
            });
            NotificationService.showCustomError(t("qrcode.scan-failed"));
          } finally {
            loading.value = false;
          }
        }
      },
      { deep: true }
    );

    /**
     * Close the QR-code scan dialog without altering modal state side effects.
     *
     * @returns {void}
     */
    function closeModal(): void {
      ModalModule.setStatus(ModalStatus.Hidden);
    }

    return {
      t,
      loading,
      modalVisibility,
      closeModal,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.scan-qr-code {
  .scan-booking {
    font-size: 2rem;
    color: #1c1c1c;
    font-weight: bold;
  }
  .container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 1rem;
    border-radius: 10px;
  }
  .qrcode-img {
    height: 50%;
    width: 50%;
    justify-self: center;
  }
  .qrcode-instruction {
    margin-top: 8px;
    font-size: 1rem;
    font-weight: bold;
  }
  .spinner-overlay {
    display: flex;
    justify-content: center;
  }
  .spinner {
    font-size: 48px;
    margin: 82px 0;
  }
}
</style>
